import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiHeart, FiMapPin, FiUsers } from "react-icons/fi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import HelpModal from "../components/foundation/HelpModal";
import { ProgressBar } from "../components/foundation/CaseCard";
import { embedUrl } from "../components/foundation/foundationUtils";
import { apiRequest, fetchFoundationSettings, formatMoney } from "../api";

const HELP_LABELS = { money: "donated", goods: "gave supplies", service: "offered their time", other: "offered help" };

export default function FoundationCasePage() {
  const { id } = useParams();
  const [item, setItem] = useState(undefined);   // undefined = loading, null = not found
  const [settings, setSettings] = useState(null);
  const [active, setActive] = useState(0);
  const [helping, setHelping] = useState(false);

  const load = useCallback(async () => {
    const { ok, data } = await apiRequest(`/core/foundation/cases/${id}/`);
    setItem(ok ? data : null);
  }, [id]);

  useEffect(() => {
    setItem(undefined);
    setActive(0);
    load();
    fetchFoundationSettings().then(setSettings).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (item) document.title = `${item.title} - Foundation`;
  }, [item]);

  const currency = settings?.currency_label || "PKR";
  const photos = item ? [item.cover_image, ...(item.images || []).map((i) => i.image)].filter(Boolean) : [];
  const embed = item ? embedUrl(item.video_url) : null;
  const monetary = item && Number(item.amount_needed) > 0;
  const place = item ? [item.city, item.country].filter(Boolean).join(", ") : "";

  return (
    <>
      <Header />
      <div className="h-16" />
      <main className="bg-navy text-white min-h-[70vh] py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/foundation" className="inline-block text-accent-light text-sm mb-6 hover:underline">&larr; Back to Foundation</Link>

          {item === undefined && <p className="text-gray-400">Loading...</p>}
          {item === null && (
            <div className="text-center py-20">
              <h1 className="text-3xl font-bold">Request not found</h1>
              <p className="text-gray-400 mt-2">It may not be published yet, or it was removed.</p>
            </div>
          )}

          {item && (
            <div className="grid lg:grid-cols-[1fr_360px] gap-10 items-start">
              <div className="space-y-8">
                {photos.length > 0 && (
                  <div>
                    <div className="rounded-xl overflow-hidden bg-navy-dark border border-accent-light/25 shadow-[0_0_25px_rgba(34,211,238,0.2)]">
                      <img src={photos[active]} alt={item.title} className="w-full max-h-[520px] object-contain bg-navy" />
                    </div>
                    {photos.length > 1 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {photos.map((src, i) => (
                          <button key={src} onClick={() => setActive(i)} className={`rounded-md overflow-hidden border-2 ${i === active ? "border-accent-light" : "border-transparent opacity-70 hover:opacity-100"}`}>
                            <img src={src} alt={`${item.title} (${i + 1})`} className="w-20 h-20 object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <span className="px-3 py-1 rounded-full bg-accent/15 text-accent-light text-xs font-semibold">{item.category_label}</span>
                  {item.status === "fulfilled" && <span className="ml-2 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-semibold">Fulfilled</span>}
                  <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-3">{item.title}</h1>
                  <p className="text-sm text-gray-400 mt-2 flex items-center gap-1"><FiMapPin size={13} /> {item.display_name}{item.age ? `, ${item.age}` : ""}{place && ` · ${place}`}</p>
                  <p className="text-gray-200 leading-relaxed whitespace-pre-line mt-6">{item.story}</p>
                </div>

                {(item.video || embed || item.video_url) && (
                  <div>
                    <h2 className="text-xl font-bold mb-3">Video</h2>
                    {item.video ? (
                      <video src={item.video} controls className="w-full rounded-xl bg-black max-h-[480px]" />
                    ) : embed ? (
                      <div className="aspect-video rounded-xl overflow-hidden bg-black">
                        <iframe src={embed} title="Video" className="w-full h-full" allowFullScreen />
                      </div>
                    ) : (
                      <a href={item.video_url} target="_blank" rel="noopener noreferrer" className="text-accent-light hover:underline">Watch the video</a>
                    )}
                  </div>
                )}

                {item.supporters.length > 0 && (
                  <div>
                    <h2 className="text-xl font-bold mb-3 flex items-center gap-2"><FiUsers /> Kind supporters</h2>
                    <div className="space-y-3">
                      {item.supporters.map((s, i) => (
                        <div key={i} className="rounded-lg bg-white/5 border border-navy-border p-4">
                          <p className="text-sm"><span className="font-semibold">{s.name}</span> <span className="text-gray-400">{HELP_LABELS[s.help_type]}{s.amount ? ` ${currency} ${formatMoney(s.amount)}` : ""}</span></p>
                          {s.message && <p className="text-sm text-gray-300 mt-1 italic">“{s.message}”</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <aside className="rounded-xl bg-navy-dark border border-accent-light/25 shadow-[0_0_25px_rgba(34,211,238,0.2)] p-6 lg:sticky lg:top-24">
                {monetary ? (
                  <>
                    <p className="text-3xl font-extrabold">{currency} {formatMoney(item.amount_raised)}</p>
                    <p className="text-sm text-gray-400 mb-3">raised of {currency} {formatMoney(item.amount_needed)}</p>
                    <ProgressBar percent={item.progress ?? 0} />
                  </>
                ) : (
                  <p className="text-lg font-semibold">Needs non-monetary help</p>
                )}
                <p className="text-sm text-gray-400 mt-3">{item.supporters_count} supporter{item.supporters_count === 1 ? "" : "s"}</p>

                {item.status === "approved" ? (
                  <button onClick={() => setHelping(true)} className="w-full mt-6 flex items-center justify-center gap-2 py-3 rounded-full bg-brand-gradient font-semibold shadow-lg shadow-accent/20">
                    <FiHeart /> Help {item.display_name}
                  </button>
                ) : (
                  <p className="mt-6 text-sm text-emerald-400 font-semibold">This request has been fulfilled. Thank you to everyone who helped!</p>
                )}
                <p className="text-xs text-gray-500 mt-4">Every request is verified by our team, and offers of help are coordinated through the foundation.</p>
              </aside>
            </div>
          )}
        </div>
      </main>
      <Footer />

      {helping && item && <HelpModal item={item} settings={settings} onClose={() => setHelping(false)} onDone={load} />}
    </>
  );
}
